import React, { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, AlertCircle } from "lucide-react";
import useTenantState from "@/states/tenant.state";
import useAuthState from "@/states/auth.state";
import { useParams } from "react-router-dom";
import { env } from "@/utils/constants/env";
import useFormEditorState from "./state/formeditor.state";
import { loadRemoteModule, invalidateRemoteScript } from "@/utils/functions/loadRemote";

interface MicroFrontendFormProps {
  props: {
    item: {
      url: string;
      component_scope?: string;  // Scope name dari Module Federation (e.g., 'remote_form')
      component_name: string;    // Module path (e.g., './RemoteForm' atau 'RemoteForm')
      height?: string;
      width?: string;
      [key: string]: any;
    };
    [key: string]: any;
  };
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  componentName: string;
  height: string;
  width: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error Boundary untuk menangkap error saat rendering komponen MFE
 */
class MFEErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('MFE Error Boundary caught an error:', error, errorInfo);
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      return (
        <MFEErrorFallback
          error={this.state.error}
          reset={this.reset}
          componentName={this.props.componentName}
          height={this.props.height}
          width={this.props.width}
        />
      );
    }

    return this.props.children;
  }
}

// Cache untuk menyimpan module yang sudah di-load
const moduleCache = new Map<string, Promise<any>>();

/**
 * Normalize module path ke format './ModuleName'
 */
const normalizeModule = (componentName: string): string =>
  componentName.startsWith('./') ? componentName : `./${componentName}`;

/**
 * Hook untuk load MFE component menggunakan Dynamic Remote Container.
 * Menggunakan loadRemoteModule dari @/utils/functions/loadRemote.
 */
const useMicroFrontendLoader = (
  url: string,
  componentName: string,
  componentScope?: string,
) => {
  const [component, setComponent] = useState<React.ComponentType<any> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const isMounted = useRef(true);

  useEffect(() => {
    if (!url || !componentName || !componentScope) {
      setError(new Error('url, component_name, dan component_scope wajib diisi'));
      setLoading(false);
      return;
    }

    isMounted.current = true;
    setLoading(true);
    setError(null);

    const module = normalizeModule(componentName);
    const cacheKey = `${url}:${componentScope}:${module}`;

    let modulePromise = moduleCache.get(cacheKey);
    if (!modulePromise) {
      modulePromise = loadRemoteModule({ url, scope: componentScope, module });
      moduleCache.set(cacheKey, modulePromise);
    }

    modulePromise
      .then((mod) => {
        if (!isMounted.current) return;
        const Component = mod.default ?? mod;
        setComponent(() => Component);
        setLoading(false);
      })
      .catch((err: Error) => {
        // Hapus cache gagal agar retry bisa berjalan
        moduleCache.delete(cacheKey);
        if (isMounted.current) {
          setError(err);
          setLoading(false);
        }
      });

    return () => {
      isMounted.current = false;
    };
  }, [url, componentName, componentScope, retryCount]);

  const retry = () => {
    invalidateRemoteScript(url);
    // Clear module cache untuk scope + module ini
    const module = normalizeModule(componentName);
    moduleCache.delete(`${url}:${componentScope}:${module}`);
    setRetryCount((prev) => prev + 1);
  };

  return { component, loading, error, retry };
};



/**
 * Fallback component untuk Error Boundary
 */
const MFEErrorFallback = ({
  error,
  reset,
  componentName,
  height,
  width
}: {
  error: Error;
  reset: () => void;
  componentName: string;
  height: string;
  width: string;
}) => (
  <div
    className="flex items-center justify-center bg-orange-50 border border-orange-200 rounded-md"
    style={{ height, width }}
  >
    <div className="flex flex-col items-center gap-3 text-orange-600 max-w-md p-4">
      <AlertCircle className="h-6 w-6" />
      <p className="text-sm font-medium">Component Render Error</p>
      <p className="text-xs text-center break-words">{error.message}</p>
      <div className="text-xs text-gray-500 mt-1">
        <p>Component: {componentName}</p>
      </div>
      <button
        onClick={reset}
        className="mt-2 px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-colors"
      >
        Reset Component
      </button>
    </div>
  </div>
);

export const MicroFrontendForm = ({ props }: MicroFrontendFormProps) => {
  const { item } = props;
  const { url, component_scope, component_name, height = '500px', width = '100%' } = item;
  const { currentTenant } = useTenantState();
  const {context} = useFormEditorState();
  const [token, setToken] = useState<string | null>(null);

  // Fetch token on mount
  useEffect(() => {
    const fetchToken = async () => {
      const authToken = await useAuthState.getState().getToken();
      setToken(authToken);
    };
    fetchToken();
  }, []);

  const params = useParams();

  const { component: LoadedComponent, loading, error, retry } = useMicroFrontendLoader(
    url,
    component_name,
    component_scope
  );

  const x_active_tenant = currentTenant?.slug;
  const probis = params.processKey;
  const x_task_id = params.id;

  const {taskId , processDefinitionKey,  task } = context;
  // Prepare parameters for injection
  const injectionParams = useMemo(() => ({
    'token': token || '',
    'activeTenant': x_active_tenant || '',
    'processDefinitionKey': processDefinitionKey || '',
    'probis': processDefinitionKey || '',
    'taskId': taskId || '',
    'apiBaseUrl': env.apiTasklistBaseUrl || '',
    'task' : task || null,
  }), [token, x_active_tenant, probis, x_task_id]);

  if (loading) {
    return (
      <div
        className="flex items-center justify-center bg-gray-50 border border-gray-200 rounded-md"
        style={{ height, width }}
      >
        <div className="flex flex-col items-center gap-2 text-gray-600">
          <Loader2 className="h-6 w-6 animate-spin" />
          <p className="text-sm">Loading component...</p>
          <p className="text-xs text-gray-400">{component_name}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="flex items-center justify-center bg-red-50 border border-red-200 rounded-md"
        style={{ height, width }}
      >
        <div className="flex flex-col items-center gap-3 text-red-600 max-w-md p-4">
          <AlertCircle className="h-6 w-6" />
          <p className="text-sm font-medium">Failed to load component</p>
          <p className="text-xs text-center break-words">{error.message}</p>
          <div className="text-xs text-gray-500 mt-1">
            <p>URL: {url}</p>
            {component_scope && <p>Scope: {component_scope}</p>}
            <p>Component: {component_name}</p>
          </div>
          <button
            onClick={retry}
            className="mt-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }


  if (!LoadedComponent) {
    return (
      <div
        className="flex items-center justify-center bg-yellow-50 border border-yellow-200 rounded-md"
        style={{ height, width }}
      >
        <div className="flex flex-col items-center gap-2 text-yellow-600">
          <AlertCircle className="h-6 w-6" />
          <p className="text-sm">Component not available</p>
        </div>
      </div>
    );
  }



  return (
    <div style={{ height, width }}>
      <MFEErrorBoundary
        componentName={component_name}
        height={height}
        width={width}
      >
        <LoadedComponent item={item} props={props} alurkerjaParams={injectionParams} />
      </MFEErrorBoundary>
    </div>
  );
};